// @ts-check

import {z} from "zod"

/** @typedef {z.infer<typeof StrapiIdType>} StrapiIdType */
export const StrapiIdType = z.string().or(z.number())

/** @typedef {z.infer<typeof PaginationMetaByPage>} PaginationMetaByPage */
export const PaginationMetaByPage = z.object({
  page: z.number().default(1),
  pageSize: z.number().default(25),
  pageCount: z.number().default(0),
  total: z.number().default(25),
})

/** @typedef {z.infer<typeof PaginationMetaByPage>} PaginationMetaByOffset */
export const PaginationMetaByOffset = z.object({
  start: z.number().default(0),
  limit: z.number().default(25),
  total: z.number().default(0),
})

/** @typedef {z.infer<typeof PaginationMeta>} PaginationMeta */
export const PaginationMeta = z.union([
  PaginationMetaByPage,
  PaginationMetaByOffset,
])

/** @typedef {z.infer<typeof PaginationQueryContextByPage>} PaginationQueryContextByPage */
export const PaginationQueryContextByPage = z.object({
  page: z.number().optional(),
  pageSize: z.number().optional(),
})

/** @typedef {z.infer<typeof PaginationQueryContextByOffset>} PaginationQueryContextByOffset */
export const PaginationQueryContextByOffset = z.object({
  start: z.number().optional(),
  limit: z.number().optional(),
})

/**
 * @param {PaginationQueryContext} paginationQuery
 * @returns {paginationQuery is PaginationQueryContextByPage}
 */
export function isPaginationByPage(paginationQuery) {
  // @ts-ignore
  return typeof paginationQuery.page === "number"
}

/**
 * @param {PaginationQueryContext} paginationQuery
 * @returns {paginationQuery is PaginationQueryContextByOffset}
 */
export function isPaginationByOffset(paginationQuery) {
  // @ts-ignore
  return typeof paginationQuery.start === "number"
}

/** @typedef {z.infer<typeof PaginationQueryContext>} PaginationQueryContext */
export const PaginationQueryContext = z.union([
  PaginationQueryContextByPage,
  PaginationQueryContextByOffset,
])

/** @typedef {z.infer<typeof StrapiItemMeta>} StrapiItemMeta */
export const StrapiItemMeta = z.object({})

/** @typedef {z.infer<typeof StrapiGetItemsParams>} StrapiGetItemsParams */
export const StrapiGetItemsParams = z.object({
  pagination: PaginationQueryContextByPage.optional(),
})

/** @typedef {z.infer<typeof StrapiGetItemsResponse>} StrapiGetItemsResponse */
export const StrapiGetItemsResponse = z.object({
  meta: z.object({
    pagination: PaginationMeta,
  }),
  error: z.object({}).optional(),
})

/**
 * ==== Strapi Media File/Image
 */

/** @typedef {z.infer<typeof MediaImageFormat>} MediaImageFormat */
const MediaImageFormat = z.object({
  name: z.string(),
  hash: z.string(),
  ext: z.string(),
  size: z.number(),
  url: z.string(),
  mime: z.string(),
  width: z.number(),
  height: z.number(),
})

/** @typedef {z.infer<typeof MediaAttributes>} MediaAttributes */
const MediaAttributes = z.object({
  name: z.string(),
  alternativeText: z.string(),
  hash: z.string(),
  ext: z.string(),
  size: z.number(),
  url: z.string(),
  mime: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
})

const GatsbyLocalStrapiImage = z.object({
  childImageSharp: z
    .object({
      gatsbyImageData: z.any().nullish(),
    })
    .nullish(),
  publicURL: z.string(),
})

/** @typedef {z.infer<typeof StrapiMedia>} StrapiMedia */
export const StrapiMedia = z
  .object({
    id: StrapiIdType,
    attributes: MediaAttributes,
    localFile: GatsbyLocalStrapiImage.nullish(),
  })
  .transform(({id, attributes, ...obj}) => ({
    ...obj,
    id,
    ...attributes,
  }))

export const StrapiMediaRequired = z
  .object({
    data: StrapiMedia,
  })
  .transform(o => o.data)

export const StrapiMediaOptional = z
  .object({
    data: StrapiMedia.nullable(),
  })
  .transform(o => o.data)
export const StrapiMediasRequired = z
  .object({
    data: z.array(StrapiMedia),
  })
  .transform(o => o.data)

export const StrapiMediasOptional = z
  .object({
    data: z.array(StrapiMedia).nullable(),
  })
  .transform(o => o.data)

/**
 * @param {StrapiMedia} media
 * @returns {media is StrapiMediaImage}
 */
export function isMediaImage(media) {
  return media.mime.startsWith("image/")
}

/**
 * @param {StrapiMedia} media
 * @returns {media is StrapiMediaFile}
 */
export function isMediaFile(media) {
  return !isMediaImage(media)
}

/** @typedef {z.infer<typeof StrapiMetaSocialNetworkEnum>} StrapiMetaSocialNetworkEnum */
export const StrapiMetaSocialNetworkEnum = z.enum(["Facebook", "Twitter"])

/** @typedef {z.infer<typeof StrapiMetaSocial>} StrapiMetaSocial */
export const StrapiMetaSocial = z.object({
  id: StrapiIdType,
  title: z.string(),
  description: z.string(),
  image: StrapiMediaOptional,
  socialNetwork: StrapiMetaSocialNetworkEnum,
})

/** @typedef {z.infer<typeof StrapiSEO>} StrapiSEO */
export const StrapiSEO = z.object({
  id: StrapiIdType,
  metaTitle: z.string(),
  metaDescription: z.string(),
  metaImage: StrapiMediaOptional.optional(),
  metaSocial: z
    .array(StrapiMetaSocial)
    .nullish()
    .transform(v => v ?? [])
    .default([]),
  keywords: z.string().nullable(),
  metaRobots: z.string().nullable(),
  structuredData: z.any().nullable(),
  metaViewport: z.string().nullable(),
  canonicalURL: z.string().nullable(),
})
